import { useCallback, useEffect, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  deleteManualSalary,
  fetchEmploymentDetailsForManualSalary,
  fetchManualSalariesByEmployment,
} from "../../../talents/talentsSlice";
import { ManualSalary, ManualSalaryUnitType } from "../../../talents/talentAPI";
import { leadingZerosDateFormat } from "../../../../common/helpers/formatDateTime";
import useMessage from "../../../../hooks/useMessage";
import { PayslipDetailed } from "../../../salaries/payslips/payslipAPI";
import { fetchPayslipById } from "../../../salaries/payslips/payslipsSlice";
import useIsLoading from "../../../../hooks/useIsLoading";
import { Box, Grid } from "@mui/material";
import { HOME_LAYOUT_WIDTH } from "../../../../app/common";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import { SimpleTable } from "../../../../common/components/functional/simpleTable/SimpleTable";
import { ManualSalaryHeader } from "../EmploymentAdditionalInfo/tableItems/ManualSalaryHeader";
import { ManualSalaryItem } from "../EmploymentAdditionalInfo/tableItems/ManualSalaryItem";
import { canExpertEditManualSalaries } from "../../../user/accessRightChecks";
import { ManualSalaryDialog } from "./ManualSalaryDialog";
import { PayslipDetails } from "../../../salaries/payslips/PayslipDetails";
import { ConfirmationDialog } from "../../../../common/components/functional/confirmationDialog/ConfirmationDialog";

export interface Option {
  value: string;
  label: string;
}

export interface ExpenseTypeOption extends Option {
  group: string;
  unit: ManualSalaryUnitType;
}

export interface CcOption extends Option {
  assignmentId: string;
}

export interface AssignmentOption extends Option {
  isShift: boolean;
  isTargetedShift?: boolean;
}

type Props = {
  employmentId: string;
  employmentStart: string;
  employmentEnd: string;
  employerName: string;
};

const SPACING = "48px";

const sortByCreatedAt = (salaries: ManualSalary[]): ManualSalary[] =>
  [...salaries].sort((a, b) => {
    // Use createdAt (epoch ms) when available, fall back to date string for older rows
    if (a.createdAt != null && b.createdAt != null) {
      return a.createdAt - b.createdAt;
    }
    if (a.createdAt != null) return -1;
    if (b.createdAt != null) return 1;
    return a.date.localeCompare(b.date);
  });

export const ManualSalaries = ({
  employmentId,
  employmentStart,
  employmentEnd,
  employerName,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { sendMessage } = useMessage();
  const workTitleTranslations = useAppSelector(
    (state) => state.localized.workTitle,
  );

  const isLoading = useIsLoading(["talents"]);

  const [isManualSalaryDialogOpen, setIsManualSalaryDialogOpen] =
    useState<boolean>(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const [manualSalaries, setManualSalaries] = useState<ManualSalary[]>([]);
  const [manualSalary, setManualSalary] = useState<ManualSalary | null>(null);

  const [payslipDetailed, setPayslipDetailed] = useState<
    PayslipDetailed | undefined
  >(undefined);
  const [isPayslipDetailsModalOpen, setIsPayslipDetailsModalOpen] =
    useState(false);

  const [assignmentOptions, setAssignmentOptions] = useState<
    AssignmentOption[]
  >([]);
  const [ccOptions, setCcOptions] = useState<CcOption[]>([]);
  const [eezyCCOptions, setEezyCCOptions] = useState<Option[]>([]);
  const [expenseTypeOptions, setExpenseTypeOptions] = useState<
    ExpenseTypeOption[]
  >([]);
  const [typeOptions, setTypeOptions] = useState<Option[]>([]);

  const [forDelete, setForDelete] = useState<{
    manualSalaryId: string;
    version: number;
  }>({ manualSalaryId: "", version: 0 });

  const onAddNew = () => {
    if (ccOptions.length === 0 || eezyCCOptions.length === 0) {
      fetchEmploymentDetails();
    }
    setManualSalary(null);
    setIsManualSalaryDialogOpen(true);
  };

  const onEdit = (index: number) => {
    setManualSalary(manualSalaries[index]);
    setIsManualSalaryDialogOpen(true);
  };

  const handleConfirmation = (index: number) => {
    setIsConfirmationDialogOpen(true);
    setForDelete({
      manualSalaryId: manualSalaries[index].manualSalaryId,
      version: manualSalaries[index].version,
    });
  };

  const cancelDelete = () => {
    setIsConfirmationDialogOpen(false);
    setForDelete({ manualSalaryId: "", version: 0 });
  };

  const handleDeleteManualSalary = () => {
    setIsConfirmationDialogOpen(false);
    dispatch(deleteManualSalary(forDelete)).then((action) => {
      if (isFulfilled(action)) {
        setManualSalaries((s) =>
          s.filter((s) => s.manualSalaryId !== forDelete.manualSalaryId),
        );
        sendMessage(t("deletedSuccessfully"), "success");
      }
    });
  };

  const copyManualSalary = (index: number) => {
    setManualSalary({ ...manualSalaries[index], manualSalaryId: "" });
    setIsManualSalaryDialogOpen(true);
  };

  const updateManualSalaries = (manualSalary: ManualSalary) => {
    setManualSalaries((s) =>
      sortByCreatedAt(
        s.find((s) => s.manualSalaryId === manualSalary.manualSalaryId)
          ? s.map((s) =>
              s.manualSalaryId === manualSalary.manualSalaryId
                ? manualSalary
                : s,
            )
          : [...s, manualSalary],
      ),
    );
  };

  const closeDialog = () => {
    setIsManualSalaryDialogOpen(false);
  };

  const openPayslip = (payslipId: string) => {
    setIsPayslipDetailsModalOpen(true);
    dispatch(
      fetchPayslipById({
        id: payslipId,
        languageCode: i18n.language,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setPayslipDetailed(action.payload);
      }
    });
  };

  const fetchEmploymentDetails = useCallback(() => {
    dispatch(fetchEmploymentDetailsForManualSalary(employmentId)).then(
      (action) => {
        if (isFulfilled(action)) {
          let assignmentOptions: any[] = [];
          let ccOptions: any[] = [];
          let expenseTypeOptions: any[] = [];
          let typeOptions: any[] = [];
          const sortedAssignments = Object.fromEntries(
            Object.entries(action.payload.assignments).sort(
              ([, a], [, b]) =>
                (b.isShift || b.isTargetedShift ? 0 : 1) -
                  (a.isShift || a.isTargetedShift ? 0 : 1) ||
                new Date(a.startDate).getTime() -
                  new Date(b.startDate).getTime(),
            ),
          );
          // Handle assignments and customer cost centers
          for (const key in sortedAssignments) {
            let option = sortedAssignments[key];
            assignmentOptions.push({
              value: key,
              label: `${format(
                new Date(option.startDate),
                leadingZerosDateFormat,
              )} ${workTitleTranslations[option.workTitleId]} ${
                option.workLocationName
              }`,
              isShift: option.isShift,
              isTargetedShift: option.isTargetedShift,
            });
            for (const key2 in option.customerCostCenters) {
              let ccOption = option.customerCostCenters[key2];
              ccOptions.push({
                value: key2,
                label: `${ccOption}`,
                assignmentId: key,
              });
            }
          }
          setAssignmentOptions(assignmentOptions);
          setCcOptions(ccOptions);
          // Handle Eezy cost centers
          setEezyCCOptions(
            action.payload.eezyCostCenters.map((cc) => ({
              value: cc.id,
              label: cc.code,
            })),
          );
          // Handle type categories and expense types
          for (const key in action.payload.expenseTypeCodes) {
            let group = action.payload.expenseTypeCodes[key];
            typeOptions.push({
              value: key,
              label: key,
            });
            expenseTypeOptions.push(
              ...group.map((expenseType) => ({
                value: expenseType.code,
                label: expenseType.code,
                group: key,
                unit: expenseType.unit,
              })),
            );
          }
          expenseTypeOptions.sort((a, b) => a.label.localeCompare(b.label));
          setExpenseTypeOptions(expenseTypeOptions);
          typeOptions.sort((a, b) => a.label.localeCompare(b.label));
          setTypeOptions(typeOptions);
        }
      },
    );
  }, [dispatch, employmentId, workTitleTranslations]);

  useEffect(() => {
    if (!employmentId) return;
    dispatch(fetchManualSalariesByEmployment(employmentId)).then((action) => {
      if (isFulfilled(action)) {
        const manualSalaries = sortByCreatedAt(action.payload.manualSalaries);
        setManualSalaries(manualSalaries);
        if (manualSalaries.length > 0) {
          fetchEmploymentDetails();
        }
      }
    });
  }, [employmentId, dispatch, workTitleTranslations, fetchEmploymentDetails]);

  return (
    <Box maxWidth={`calc(${HOME_LAYOUT_WIDTH} - ${SPACING})`} width="100%">
      <FormSection title={t("talentProfile:manualSalaries")}>
        <Grid item container mt={3}>
          <SimpleTable
            tableSx={{ minWidth: "1400px" }}
            header={<ManualSalaryHeader />}
            items={manualSalaries.map((salary, index) => (
              <ManualSalaryItem
                index={index}
                eezyCompanyName={employerName}
                onDelete={() => handleConfirmation(index)}
                onEdit={() => onEdit(index)}
                onCopy={() => copyManualSalary(index)}
                manualSalary={salary}
                assignmentOptions={assignmentOptions}
                ccOptions={ccOptions}
                eezyCcOptions={eezyCCOptions}
                expenseTypeOptions={expenseTypeOptions}
                openPayslip={openPayslip}
              />
            ))}
            addItem={onAddNew}
            canAdd={true}
            accessRightChecks={[canExpertEditManualSalaries]}
          />
          <ManualSalaryDialog
            isOpen={isManualSalaryDialogOpen}
            close={closeDialog}
            manualSalary={manualSalary || null}
            assignmentOptions={assignmentOptions}
            ccOptions={ccOptions}
            eezyCcOptions={eezyCCOptions}
            expenseTypeOptions={expenseTypeOptions}
            typeOptions={typeOptions}
            employmentId={employmentId}
            employmentStart={employmentStart}
            employmentEnd={employmentEnd}
            employerName={employerName}
            updateManualSalaries={updateManualSalaries}
            isLoading={isLoading}
          />
        </Grid>
      </FormSection>
      <PayslipDetails
        payslip={payslipDetailed}
        isOpen={isPayslipDetailsModalOpen}
        onClose={() => {
          setIsPayslipDetailsModalOpen(false);
          setPayslipDetailed(undefined);
        }}
      />
      <ConfirmationDialog
        title={t("confirmDialogDelete")}
        contentText={t("talentProfile:confirmDeletingManualSalary")}
        cancelText={t("cancel")}
        confirmText={t("delete")}
        isOpen={isConfirmationDialogOpen}
        onConfirm={handleDeleteManualSalary}
        onCancel={cancelDelete}
      />
    </Box>
  );
};
